import { produce } from 'immer'

import { appStore } from '..'
import type { AppState, WritableAppState } from '../types'
import type { AppActionHandler, AppActions } from '../types/actions'

/**
 * Factory to help create new actions without having to re-implement the mechanisms
 * to set state using immer or handle potential errors.
 *
 * This is useful to define actions at module level, thus allowing them to live
 * outside of the store and improve code splitting.
 *
 * @see {@link https://zustand.docs.pmnd.rs/guides/practice-with-no-store-actions}
 */
export const actionFactory = <A extends AppActions>(
  /** This action handler implementation */
  fn: AppActionHandler<A>,
  /** This handler name, for error message purposes */
  actionName?: string
) => {
  return (
    /** Which data to provide the action with */
    payload: A['payload'],
    /**
     * Current writable state to base this change off of
     *
     * Useful when calling this action from another action that might already have patched
     * the writable state.
     */
    writableDraft?: WritableAppState
  ) => {
    try {
      if (typeof writableDraft === 'undefined') {
        appStore.setState(
          produce<AppState>((draftState) => fn(draftState, payload)),
          undefined,
          {
            type: actionName ?? 'anonymous',
            payload
          }
        )

        return
      }

      fn(writableDraft, payload)
    } catch (originalError) {
      const error = new Error(
        actionName
          ? `An error occurred while performing the following action: ${actionName}`
          : `An error occurred while performing an action`
      )

      error.cause = originalError

      throw error
    }
  }
}

/**
 * Convenience method to set state using immer.
 * Passes down the `setterName` name for debugging purposes.
 *
 * This is useful when you need to set state after an async operation (i.e. fetching data).
 */
export const setter = (
  fn: (draftState: WritableAppState) => void,
  /**
   * Name of the setter, for debugging purposes
   * @example `hydrateJourney.setEditorReady`
   */
  setterName: { type: string; payload?: Record<string, unknown> } | string
) => {
  appStore.setState(
    produce<AppState>((draftState) => fn(draftState)),
    undefined,
    setterName
  )
}
